import { Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import AccountStatusModal from "../components/AccountStatus/AccountStatusModal";
import { useBillings } from "../contexts/BillingContext";

/**
 * RouteGuard Component
 * Ensures the user has valid account standing before accessing protected routes.
 */
const RouteGuard = () => {
    const { 
        initialized, 
        authLoading, 
        accountInGoodStanding, 
        currentUser 
    } = useAuth();


    // Billing readiness and live subscription status
    const {
      billingLoaded,
      reconciling,
      currentSubscription,
    } = useBillings();

    // Do not gate while billing is loading or reconciling
    const readyToGate = billingLoaded && !reconciling;

    const billingStatus = currentSubscription?.status ?? null;

    const billingActive =
      billingStatus === "active" ||
      billingStatus === "trialing";


    const [isReady, setIsReady] = useState(false);
    const [cachedStanding, setCachedStanding] = useState(undefined);
    const [showModal, setShowModal] = useState(false);

    /**
     * Retrieves the cached account standing from sessionStorage.
     */
    const getAccountStandingFromCache = (uid) => {
      if (!uid) return undefined;

      const cachedData = sessionStorage.getItem(
        `accountInGoodStanding_${uid}`,
      );

      if (!cachedData) return undefined;

      try {
        return JSON.parse(cachedData);
      } catch (e) {
        console.warn(
          "[RouteGuard] Failed to parse cached account standing:",
          e?.message || e,
        );
        return undefined;
      }
    };


    /**
     * Effect: Load cached account standing when the user changes.
     */
    useEffect(() => {
        if (currentUser?.uid) {
            const cachedValue = getAccountStandingFromCache(currentUser.uid);
            if (cachedValue !== undefined) {
//                console.log(`[RouteGuard] Using cached accountInGoodStanding: ${cachedValue}`);
                setCachedStanding(cachedValue);
            }
        }
    }, [currentUser?.uid]);

     /**
      * Effect: Monitor authentication and account standing readiness.
      */
     useEffect(() => {
 //        console.log("[RouteGuard] Checking readiness:", { initialized, authLoading, accountInGoodStanding, cachedStanding });
 
         if (!initialized || authLoading) {
 //            console.log("[RouteGuard] Waiting for AuthContext to initialize...");
             return;
         }
        // Once auth is ready, allow the UI to render when we have *any* answer.
        // Billing is preferred once ready, but fallback can still answer earlier.
        const hasStandingAnswer =
          (readyToGate && billingStatus !== null) ||
          (accountInGoodStanding !== undefined) ||
          (cachedStanding !== undefined);

        if (hasStandingAnswer) {
          setIsReady(true);
        }



    }, [
      initialized,
      authLoading,
      accountInGoodStanding,
      cachedStanding,
      readyToGate,
//      billingActive,
      billingStatus,
    ]);


/**
 * Determine the final account standing (billing-first).
 *
 * Step B:
 *  - Once Billing is ready, Billing becomes the source of truth.
 *
 * Step C (SAFE):
 *  - If Billing is "ready" but does not yet have a usable status,
 *    fall back to Auth/cache so we don't incorrectly lock users out
 *    during transient / missing-data windows.
 */
const billingHasKnownStatus = billingStatus !== null;

const fallbackStanding =
  (accountInGoodStanding !== undefined)
    ? accountInGoodStanding
    : cachedStanding;

const finalAccountStanding = readyToGate
  ? (billingHasKnownStatus ? billingActive : fallbackStanding)
  : fallbackStanding;



//    console.log("[RouteGuard] Account standing check:", { finalAccountStanding });

     /**
      * Effect: Show AccountStatusModal if the account is NOT in good standing.
      */
     useEffect(() => {
        if (!readyToGate) return;
        if (finalAccountStanding === false) {
 //           console.log("[RouteGuard] Account is not in good standing. Showing modal...");
             setShowModal(true);
        } else {
            setShowModal(false);
         }
    }, [finalAccountStanding, readyToGate]);


    /**
     * Handle modal close event.
     */
    const handleCloseModal = () => {
//        console.log("[RouteGuard] User manually closed AccountStatusModal.");
        setShowModal(false);
    };

    /**
     *  Prevent premature rendering (avoids flickering)
     */
    if (!isReady) {
//        console.log("[RouteGuard] Waiting for account standing data...");
        return null;
    }

    return (
        <>
            <Outlet />
            <AccountStatusModal 
                open={showModal} 
                handleClose={handleCloseModal} 
                isAdmin={false} // Role logic removed
            />
        </>
    );
};

export default RouteGuard;
